/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   WorkflowDashboard.tsx
 *     Description     :   Workflow Dashboard — onboarding status, bottlenecks, and next actions
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-02-16 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.3.0 (Enterprise Dependency Architecture)
 *     Feature         :   135-ui-data-alignment
 *     Conformed to Spec 197 design blueprint
 * =========================================================================================
 */

import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import {
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Database,
  Warehouse,
  RefreshCw,
  Search,
  ArrowUpDown,
} from 'lucide-react';
import type { WorkflowResourceStatus, WorkflowSummary, CollectionSyncStatus } from '../../../shared/types/workflow';
import {
  useWorkflowSummary,
  useLakehouseOnboarding,
  useWarehouseOnboarding,
  useCollectionSyncStatus,
} from '../hooks/useWorkflowStatus';
import type { WorkflowOnboardingFilters } from '../api/workflowApi';
import { WorkflowCard } from '../components/WorkflowCard';
import {
  PageLayout,
  Card,
  Button,
  Input,
  Select,
  Toggle,
  StatCard,
  EmptyState,
  LoadingState,
  ErrorState,
  AppTooltip,
  PageHelpPanel,
  Modal,
} from '../../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';
import LakehouseDetailPage from '../../backup/pages/LakehouseDetailPage';
import WarehouseDetailPage from '../../backup/pages/WarehouseDetailPage';
import {
  workflowDashboardHelp,
  workflowMetricTooltips,
  workflowTabTooltips,
  workflowFilterTooltips,
  workflowSortTooltips,
  collectionHealthTooltip,
} from '../help/workflowDashboardHelp';

// ─── Types ───────────────────────────────────────────────────────────────────

type WorkflowTab = 'lakehouse' | 'warehouse' | 'collection';
type FilterMode = 'all' | 'complete' | 'incomplete' | 'stuck';

type StatColor = 'success' | 'accent' | 'warning' | 'danger' | 'muted';

const statValueClass: Record<StatColor, string> = {
  success: 'text-[var(--color-status-success)]',
  accent: 'text-[var(--color-accent)]',
  warning: 'text-[var(--color-status-warning)]',
  danger: 'text-[var(--color-status-danger)]',
  muted: 'text-[var(--color-text-secondary)]',
};

// ─── Collection Sync Panel ───────────────────────────────────────────────────

function CollectionSyncPanel({ data, isLoading }: { data?: CollectionSyncStatus; isLoading: boolean }) {
  if (isLoading) {
    return <LoadingState rows={5} />;
  }

  if (!data) {
    return (
      <EmptyState
        icon={<RefreshCw className="h-10 w-10" />}
        title="No collection data available"
        message="Run a data collection job to see sync status here."
      />
    );
  }

  const healthColor: StatColor =
    data.health === 'healthy' ? 'success' : data.health === 'warning' ? 'warning' : 'danger';

  const bannerToneClass =
    healthColor === 'success'
      ? 'border-[var(--color-status-success)]/40 bg-[var(--color-status-success)]/5'
      : healthColor === 'warning'
        ? 'border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/5'
        : 'border-[var(--color-status-danger)]/40 bg-[var(--color-status-danger)]/5';

  return (
    <div className="space-y-4">
      {/* Health status banner */}
      <AppTooltip title={collectionHealthTooltip} placement="bottom">
        <div className={clsx('flex cursor-help items-center gap-3 rounded-lg border p-4', bannerToneClass)}>
          {healthColor === 'success' ? (
            <CheckCircle2 className="h-6 w-6 text-[var(--color-status-success)]" />
          ) : (
            <AlertTriangle
              className={clsx(
                'h-6 w-6',
                healthColor === 'warning'
                  ? 'text-[var(--color-status-warning)]'
                  : 'text-[var(--color-status-danger)]',
              )}
            />
          )}
          <div>
            <div className="font-semibold text-[var(--color-text-primary)]">
              Collection Status: {data.health.charAt(0).toUpperCase() + data.health.slice(1)}
            </div>
            <div className="text-sm text-[var(--color-text-secondary)]">
              Last run: {data.lastRun?.status ?? 'Unknown'} · {data.hoursSinceLastRun}h ago
            </div>
          </div>
        </div>
      </AppTooltip>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Last Run"
          value={data.lastRun?.status ?? 'Unknown'}
          icon={<Clock className="h-4 w-4" />}
          valueClassName={statValueClass[data.lastRun?.status === 'Completed' ? 'success' : 'warning']}
          subtext={data.lastRun?.startedAt ? formatEastern(data.lastRun.startedAt) : 'Never'}
        />
        <StatCard
          label="Hours Since Last"
          value={data.hoursSinceLastRun}
          icon={<Clock className="h-4 w-4" />}
          valueClassName={
            statValueClass[data.hoursSinceLastRun <= 24 ? 'success' : data.hoursSinceLastRun <= 48 ? 'warning' : 'danger']
          }
        />
        <StatCard
          label="Runs (24h)"
          value={data.totalRuns24h}
          icon={<RefreshCw className="h-4 w-4" />}
          valueClassName={statValueClass.accent}
          subtext={data.failedRuns24h > 0 ? `${data.failedRuns24h} failed` : 'All successful'}
        />
        <AppTooltip title={workflowMetricTooltips.driftDetected}>
          <div>
            <StatCard
              label="Drift Detected"
              value={data.driftDetected ? 'Yes' : 'No'}
              icon={<AlertTriangle className="h-4 w-4" />}
              valueClassName={statValueClass[data.driftDetected ? 'danger' : 'success']}
            />
          </div>
        </AppTooltip>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function WorkflowDashboard() {
  const [activeTab, setActiveTab] = useState<WorkflowTab>('lakehouse');
  // Spec 216 T016 (Clarify): finished workflows are HIDDEN by default —
  // incomplete + stuck stay visible; "Show completed" reveals the rest.
  const [showCompleted, setShowCompleted] = useState(false);
  const [stuckOnly, setStuckOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filterMode: FilterMode = stuckOnly ? 'stuck' : showCompleted ? 'all' : 'incomplete';
  const [sortBy, setSortBy] = useState<'name' | 'completion' | 'currentStep'>('completion');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Detail modal opened from a workflow card — lakehouse detail / warehouse assignment.
  const [detail, setDetail] = useState<{ type: 'lakehouse' | 'warehouse'; id: string } | null>(null);
  const openDetail = (r: WorkflowResourceStatus) => setDetail({ type: r.entityType as 'lakehouse' | 'warehouse', id: r.entityId });

  // Build filters object
  const filters: WorkflowOnboardingFilters = useMemo(
    () => ({
      filter: filterMode,
      sortBy,
      sortDir,
      search: searchTerm || undefined,
    }),
    [filterMode, sortBy, sortDir, searchTerm]
  );

  // Data fetching
  const summaryQuery = useWorkflowSummary();
  const lakehouseQuery = useLakehouseOnboarding(activeTab === 'lakehouse' ? filters : {});
  const warehouseQuery = useWarehouseOnboarding(activeTab === 'warehouse' ? filters : {});
  const collectionQuery = useCollectionSyncStatus();
  const { data: summaryData, isLoading: summaryLoading } = summaryQuery;
  const { data: lakehouseData, isLoading: lakehouseLoading } = lakehouseQuery;
  const { data: warehouseData, isLoading: warehouseLoading } = warehouseQuery;
  const { data: collectionData, isLoading: collectionLoading } = collectionQuery;

  // A failed load is shown as a failure — never as an empty, healthy-looking page (Principle LIII).
  const activeQuery =
    activeTab === 'lakehouse' ? lakehouseQuery : activeTab === 'warehouse' ? warehouseQuery : collectionQuery;
  const failedQuery = summaryQuery.isError ? summaryQuery : activeQuery.isError ? activeQuery : null;

  // Derive active data for the current tab
  const activeData: WorkflowResourceStatus[] = useMemo(() => {
    if (activeTab === 'lakehouse') return lakehouseData?.data ?? [];
    if (activeTab === 'warehouse') return warehouseData?.data ?? [];
    return [];
  }, [activeTab, lakehouseData, warehouseData]);

  // Summary workflow entries from the aggregated endpoint
  const lakehouseSummary = summaryData?.workflows?.find((w: WorkflowSummary) => w.workflowType === 'lakehouse_onboarding');
  const warehouseSummary = summaryData?.workflows?.find((w: WorkflowSummary) => w.workflowType === 'warehouse_onboarding');

  // Spec 292 T032 (audit F10): the tab's tile set shows the WHOLE population's summary (the aggregated endpoint,
  // the same four fields the page header sums), not the filtered list's own summary — under the default
  // "incomplete" filter that one excluded every onboarded resource, so Total/Fully Complete/Avg Completion read a
  // different population than the header right above them.
  const activeSummary = useMemo(() => {
    if (activeTab === 'lakehouse') return lakehouseSummary ?? null;
    if (activeTab === 'warehouse') return warehouseSummary ?? null;
    return null;
  }, [activeTab, lakehouseSummary, warehouseSummary]);

  const isLoading = summaryLoading || (activeTab === 'lakehouse' ? lakehouseLoading : activeTab === 'warehouse' ? warehouseLoading : collectionLoading);

  // ─── Tab definitions ─────────────────────────────────────────────────────

  const tabs: { key: WorkflowTab; label: string; icon: React.ReactNode; count?: number }[] = [
    {
      key: 'lakehouse',
      label: 'Lakehouse Onboarding',
      icon: <Database className="h-4 w-4" />,
      count: lakehouseSummary?.totalResources,
    },
    {
      key: 'warehouse',
      label: 'Warehouse Onboarding',
      icon: <Warehouse className="h-4 w-4" />,
      count: warehouseSummary?.totalResources,
    },
    {
      key: 'collection',
      label: 'Collection & Sync',
      icon: <RefreshCw className="h-4 w-4" />,
    },
  ];

  return (
    <PageLayout
      title="Workflow Dashboard"
      description="Track onboarding progress, identify bottlenecks, and navigate to next actions"
      icon={<BarChart3 className="h-6 w-6 text-[var(--color-accent)]" />}
      actions={<PageHelpPanel content={workflowDashboardHelp} buttonSize="sm" />}
    >
      {/* Summary Metrics Panel */}
      {!summaryLoading && summaryData && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <AppTooltip title={workflowMetricTooltips.totalResources}>
            <div>
              <StatCard
                label="Total Resources"
                value={(lakehouseSummary?.totalResources ?? 0) + (warehouseSummary?.totalResources ?? 0)}
                icon={<Database className="h-4 w-4" />}
                valueClassName={statValueClass.accent}
                subtext="Lakehouses + Warehouses"
              />
            </div>
          </AppTooltip>
          <AppTooltip title={workflowMetricTooltips.fullyOnboarded}>
            <div>
              <StatCard
                label="Fully Onboarded"
                value={(lakehouseSummary?.fullyCompleteCount ?? 0) + (warehouseSummary?.fullyCompleteCount ?? 0)}
                icon={<CheckCircle2 className="h-4 w-4" />}
                valueClassName={statValueClass.success}
                subtext="All steps complete"
              />
            </div>
          </AppTooltip>
          <AppTooltip title={workflowMetricTooltips.avgCompletion}>
            <div>
              <StatCard
                label="Avg Completion"
                value={`${Math.round(
                  ((lakehouseSummary?.avgCompletionPct ?? 0) + (warehouseSummary?.avgCompletionPct ?? 0)) / 2
                )}%`}
                icon={<BarChart3 className="h-4 w-4" />}
                valueClassName={statValueClass.warning}
                subtext="Across all workflows"
              />
            </div>
          </AppTooltip>
          <AppTooltip title={workflowMetricTooltips.bottleneck}>
            <div>
              <StatCard
                label="Bottleneck"
                value={lakehouseSummary?.bottleneckStep ?? warehouseSummary?.bottleneckStep ?? 'None'}
                icon={<AlertTriangle className="h-4 w-4" />}
                valueClassName={statValueClass[(lakehouseSummary?.bottleneckStep ?? warehouseSummary?.bottleneckStep) ? 'warning' : 'muted']}
                subtext="Most stuck resources"
              />
            </div>
          </AppTooltip>
        </div>
      )}

      {/* Workflow Tabs */}
      <Card padding="none">
        {/* Tab bar */}
        <div className="border-b border-[var(--color-border)]">
          <nav className="flex flex-wrap px-2">
            {tabs.map((tab) => (
              <AppTooltip key={tab.key} title={workflowTabTooltips[tab.key]} placement="bottom">
                <button
                  onClick={() => setActiveTab(tab.key)}
                  className={clsx(
                    '-mb-px flex items-center gap-2 border-b-2 px-6 py-3 text-sm font-medium transition-colors',
                    activeTab === tab.key
                      ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                      : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                  )}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.count !== undefined && (
                    <span
                      className={clsx(
                        'ml-1 rounded-full px-2 py-0.5 text-xs',
                        activeTab === tab.key
                          ? 'bg-[var(--color-accent)]/15 text-[var(--color-accent)]'
                          : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]',
                      )}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              </AppTooltip>
            ))}
          </nav>
        </div>

        {/* Filters bar (for onboarding tabs) */}
        {activeTab !== 'collection' && (
          <div className="flex flex-wrap items-end gap-3 border-b border-[var(--color-border)] p-4">
            {/* Search */}
            <div className="min-w-[200px] flex-1">
              <Input
                isFullWidth
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftAddon={<Search className="h-4 w-4" />}
              />
            </div>

            {/* Spec 216 T016: completed workflows are hidden by default */}
            <AppTooltip title={workflowFilterTooltips[filterMode]} placement="bottom">
              <div className="flex items-center gap-2 pb-2">
                <Toggle
                  checked={showCompleted}
                  onChange={(checked) => {
                    setShowCompleted(checked);
                    if (checked) setStuckOnly(false);
                  }}
                  label="Show completed"
                />
              </div>
            </AppTooltip>
            <AppTooltip title={workflowFilterTooltips.stuck} placement="bottom">
              <Button
                variant={stuckOnly ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setStuckOnly(!stuckOnly);
                  if (!stuckOnly) setShowCompleted(false);
                }}
                leftIcon={<AlertTriangle className="h-4 w-4" />}
              >
                Stuck only
              </Button>
            </AppTooltip>

            {/* Sort */}
            <AppTooltip title={workflowSortTooltips.sortDropdown} placement="bottom">
              <div>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'completion' | 'currentStep')}
                  options={[
                    { value: 'completion', label: 'By Completion' },
                    { value: 'name', label: 'By Name' },
                    { value: 'currentStep', label: 'By Current Step' },
                  ]}
                />
              </div>
            </AppTooltip>
            <AppTooltip title={workflowSortTooltips.sortDirection} placement="bottom">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
                aria-label={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
                leftIcon={<ArrowUpDown className="h-4 w-4" />}
              >
                {sortDir === 'asc' ? 'Asc' : 'Desc'}
              </Button>
            </AppTooltip>
          </div>
        )}

        {/* Content */}
        <div className="p-4">
          {failedQuery ? (
            <ErrorState
              title="Failed to load workflows"
              message={failedQuery.error instanceof Error ? failedQuery.error.message : 'Unknown error'}
              onRetry={() => {
                void summaryQuery.refetch?.();
                void activeQuery.refetch?.();
              }}
            />
          ) : isLoading ? (
            <LoadingState rows={6} />
          ) : activeTab === 'collection' ? (
            <CollectionSyncPanel data={collectionData} isLoading={collectionLoading} />
          ) : (
            <>
              {/* Per-tab summary */}
              {activeSummary && (
                <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <StatCard
                    label="Total"
                    value={activeSummary.totalResources}
                    icon={<Database className="h-4 w-4" />}
                    valueClassName={statValueClass.accent}
                  />
                  <StatCard
                    label="Fully Complete"
                    value={activeSummary.fullyCompleteCount}
                    icon={<CheckCircle2 className="h-4 w-4" />}
                    valueClassName={statValueClass.success}
                  />
                  <StatCard
                    label="Avg Completion"
                    value={`${Math.round(activeSummary.avgCompletionPct)}%`}
                    icon={<BarChart3 className="h-4 w-4" />}
                    valueClassName={statValueClass.warning}
                  />
                  <StatCard
                    label="Bottleneck"
                    value={activeSummary.bottleneckStep || 'None'}
                    icon={<AlertTriangle className="h-4 w-4" />}
                    valueClassName={statValueClass[activeSummary.bottleneckStep ? 'warning' : 'muted']}
                  />
                </div>
              )}

              {/* Resource cards grid */}
              {activeData.length === 0 ? (
                <EmptyState
                  icon={<Database className="h-10 w-10" />}
                  title="No resources found"
                  message={
                    filterMode === 'incomplete'
                      ? 'Nothing unfinished — turn on "Show completed" to see finished workflows.'
                      : filterMode !== 'all'
                        ? 'Try changing the filter or search term.'
                        : 'Run a discovery job to populate resources.'
                  }
                />
              ) : (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  {activeData.map((resource) => (
                    <WorkflowCard key={resource.entityId} resource={resource} onOpenDetail={openDetail} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Detail modal — opened from a workflow card. Lakehouse → lakehouse detail
          (backup/storage/schedule); Warehouse → warehouse detail + assignment.
          Same kit <Modal> + embedded detail page the list pages use. */}
      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.type === 'warehouse' ? 'Warehouse Detail' : 'Lakehouse Detail'}
        size="xl"
        className="p-0"
      >
        {detail?.type === 'lakehouse' && (
          <LakehouseDetailPage embeddedId={detail.id} onClose={() => setDetail(null)} />
        )}
        {detail?.type === 'warehouse' && (
          <WarehouseDetailPage embeddedId={detail.id} onClose={() => setDetail(null)} />
        )}
      </Modal>
    </PageLayout>
  );
}

export default WorkflowDashboard;
